import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, 
  Users, 
  FileText, 
  DollarSign, 
  Crown,
  Loader2,
  ShieldAlert,
  TrendingUp,
  Calendar,
  UserPlus,
  RefreshCw,
  Smartphone,
  Globe,
  Trash2
} from 'lucide-react';
import AdminBroadcastCard from '../components/AdminBroadcastCard';
import AdminGiftPremiumCard from '../components/AdminGiftPremiumCard';
import AdminRaffleCard from '../components/AdminRaffleCard';
import AdminEmailBroadcastCard from '../components/AdminEmailBroadcastCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/signin');
        return;
      }

      // Check if user is admin
      const checkResponse = await fetch(`${API_URL}/api/admin/check`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!checkResponse.ok) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const checkData = await checkResponse.json();
      
      if (!checkData.isAdmin) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setIsAdmin(true);

      // Load admin data
      await loadAdminData(token);
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminData = async (token) => {
    try {
      // Load stats
      const statsResponse = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Load users
      const usersResponse = await fetch(`${API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const COUNTRY_NAMES = {
    CO: 'Colombia', US: 'USA', MX: 'México', ES: 'España', AR: 'Argentina',
    CL: 'Chile', PE: 'Perú', EC: 'Ecuador', VE: 'Venezuela', BO: 'Bolivia',
    BR: 'Brasil', PA: 'Panamá', CR: 'Costa Rica', GT: 'Guatemala', HN: 'Honduras',
    SV: 'El Salvador', NI: 'Nicaragua', PY: 'Paraguay', UY: 'Uruguay',
    DO: 'Dominicana', PR: 'Puerto Rico', CU: 'Cuba', CA: 'Canadá', FR: 'Francia',
    PT: 'Portugal', IT: 'Italia', DE: 'Alemania', GB: 'Reino Unido',
  };

  const countryName = (code) => {
    if (!code) return null;
    const upper = code.toUpperCase();
    if (COUNTRY_NAMES[upper]) return COUNTRY_NAMES[upper];
    if (code.length === 2) {
      try {
        const name = new Intl.DisplayNames(['es'], { type: 'region' }).of(upper);
        if (name && name !== upper) return name;
      } catch (e) { /* ignore */ }
    }
    return code;
  };

  const FlagImg = ({ code }) => {
    if (!code || code.length !== 2 || !/^[A-Za-z]{2}$/.test(code)) return null;
    return (
      <img
        src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
        srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`}
        alt={code.toUpperCase()}
        className="inline-block w-5 h-auto rounded-[2px] align-[-2px]"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  const getCountryCell = (u) => {
    const c = u.country;
    if (!c) return <span className="text-xs text-gray-400">—</span>;
    return (
      <span className="whitespace-nowrap text-sm dark:text-gray-300 inline-flex items-center gap-1.5" data-testid={`country-${u.id}`}>
        <FlagImg code={c} />{countryName(c)}
      </span>
    );
  };

  const countrySummary = React.useMemo(() => {
    const counts = {};
    users.forEach((u) => {
      const key = u.country ? u.country.toUpperCase() : '—';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [users]);

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/users/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al eliminar');
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      toast({ title: 'Usuario eliminado', description: `${deleteTarget.email} y todos sus datos fueron eliminados.` });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const getSubscriptionBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-lime-500">Premium</Badge>;
      case 'trialing':
        return <Badge className="bg-blue-500">Trial</Badge>;
      default:
        return <Badge className="bg-gray-500">Gratis</Badge>;
    }
  };

  const getActivityBadge = (user) => {
    if (!user.lastSeenAt) {
      return <span className="text-xs text-gray-400">Sin datos</span>;
    }
    const days = Math.floor((Date.now() - new Date(user.lastSeenAt).getTime()) / 86400000);
    const label = days === 0 ? 'Hoy' : days === 1 ? 'Ayer' : `Hace ${days} días`;
    const color = days <= 7
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : days <= 30
        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    const SourceIcon = user.lastSeenSource === 'app' ? Smartphone : Globe;
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${color}`} data-testid={`activity-badge-${user.id}`}>
        <SourceIcon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-background">
        <Card className="p-8 max-w-md text-center">
          <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Acceso Denegado
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            No tienes permisos para acceder al panel de administración.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="bg-lime-500 hover:bg-lime-600">
            Volver al Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background allow-select">
      {/* Header */}
      <header className="bg-white dark:bg-card border-b border-gray-200 dark:border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-6 h-6 text-lime-500" />
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  Panel de Administración
                </h1>
              </div>
            </div>
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">Factu</span>
              <span className="text-2xl font-bold text-white bg-lime-500 px-2 ml-1">Ya!</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards - Row 1: General */}
        {stats && (
          <>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">General</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* 1. Usuarios */}
              <Card className="p-4 dark:bg-card">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Usuarios</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.totalUsers}</p>
                  </div>
                </div>
              </Card>

              {/* 2. Premium */}
              <Card className="p-4 dark:bg-card">
                <div className="flex items-center gap-3">
                  <div className="bg-lime-100 dark:bg-lime-900/30 p-2 rounded-full">
                    <Crown className="w-5 h-5 text-lime-600 dark:text-lime-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Premium</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.premiumUsers}</p>
                  </div>
                </div>
              </Card>

              {/* 3. Usuarios/Mes */}
              <Card className="p-4 dark:bg-card">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-full">
                    <TrendingUp className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Usuarios/Mes</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.usersThisMonth}</p>
                  </div>
                </div>
              </Card>

              {/* 4. Facturas */}
              <Card className="p-4 dark:bg-card">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-full">
                    <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Facturas</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.totalInvoices}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Stats Cards - Row 2: Ingresos del Mes */}
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">Ingresos del Mes</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {/* Nuevos Premium */}
              <Card className="p-4 dark:bg-card">
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 dark:bg-emerald-900/30 p-2 rounded-full">
                    <UserPlus className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Nuevos Premium</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.newPremiumThisMonth}</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">${stats.newPremiumRevenue} USD</p>
                  </div>
                </div>
              </Card>

              {/* Renovaciones */}
              <Card className="p-4 dark:bg-card">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                    <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Renovaciones</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.renewalsThisMonth}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">${stats.renewalsRevenue} USD</p>
                  </div>
                </div>
              </Card>

              {/* Ingresos del Mes */}
              <Card className="p-4 dark:bg-card">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                    <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total del Mes</p>
                    <p className="text-xl font-bold text-green-600 dark:text-green-400">
                      ${stats.totalMonthlyRevenue} USD
                    </p>
                  </div>
                </div>
              </Card>

              {/* Balance - Link to Balance Page */}
              <Card 
                className="p-4 dark:bg-card cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => navigate('/admin/balance')}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-cyan-100 dark:bg-cyan-900/30 p-2 rounded-full">
                    <TrendingUp className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Balance Anual</p>
                    <p className="text-sm font-semibold text-cyan-600 dark:text-cyan-400">Ver detalles →</p>
                  </div>
                </div>
              </Card>
            </div>
          </>
        )}

        {/* Broadcast notification card */}
        <AdminBroadcastCard />

        {/* Gift premium card */}
        <AdminGiftPremiumCard />

        <AdminRaffleCard />

        <AdminEmailBroadcastCard />

        {/* Users Table */}
        <Card className="dark:bg-card">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Usuarios Registrados ({users.length})
            </h2>

            {/* Resumen por país */}
            {users.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="country-summary">
                {countrySummary.map(([code, count]) => (
                  <span key={code} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {code === '—' ? 'Sin país' : (<><FlagImg code={code} />{countryName(code)}</>)}
                    <span className="font-bold">{count}</span>
                  </span>
                ))}
              </div>
            )}
            
            {users.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No hay usuarios registrados
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>País</TableHead>
                      <TableHead>Registro</TableHead>
                      <TableHead>Facturas</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Actividad</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user, index) => (
                      <TableRow key={user.id || index}>
                        <TableCell className="font-medium dark:text-white">
                          {user.email}
                        </TableCell>
                        <TableCell className="dark:text-gray-300">
                          {user.name || 'Sin nombre'}
                        </TableCell>
                        <TableCell>
                          {getCountryCell(user)}
                        </TableCell>
                        <TableCell className="dark:text-gray-300">
                          {formatDate(user.createdAt)}
                        </TableCell>
                        <TableCell className="dark:text-gray-300">
                          {user.invoiceCount}
                        </TableCell>
                        <TableCell>
                          {getSubscriptionBadge(user.subscriptionStatus)}
                        </TableCell>
                        <TableCell>
                          {getActivityBadge(user)}
                        </TableCell>
                        <TableCell>
                          {user.email?.toLowerCase() !== 'soportefactuya@gmail.com' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={() => setDeleteTarget(user)}
                              data-testid={`delete-user-btn-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Confirmación de eliminación */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará permanentemente la cuenta <strong>{deleteTarget?.email}</strong> junto con
              todos sus documentos ({deleteTarget?.invoiceCount || 0} facturas), productos, suscripciones
              y notificaciones. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-user-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteUser(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-user-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sí, eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPanel;
